import { Results, SelfieSegmentation } from '@mediapipe/selfie_segmentation';

import './MediastreamTrackProcessor.polyfill';

const MAX_BLUR_FRAME_RATE_MILLISECONDS = 1000 / 25; // 25fps;

export interface TrackProcessor {
  name: string;
  init: (stream: MediaStream) => Promise<MediaStream>;
  destroy: () => Promise<void>;
  processedTrack?: MediaStreamTrack;
  onPublish?: () => Promise<void>;
  onUnpublish?: () => Promise<void>;
}

export class BlurProcessor implements TrackProcessor {
  private canvasCtx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private selfieSegmentation: SelfieSegmentation;
  private blurValue = 0;
  private incomingStream: MediaStream | null = null;
  private incomingTrackProcessorReader: ReadableStreamDefaultReader | null = null;
  isStarted = false;
  name: 'blur-processor';
  processedStream?: MediaStream;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvasCtx = this.canvas.getContext('2d')!;
    this.selfieSegmentation = new SelfieSegmentation({
      locateFile: (file: string): string =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
    });
    this.selfieSegmentation.setOptions({
      modelSelection: 1,
    });

    this.selfieSegmentation.initialize().then(() => {
      console.log('selfieSegmentation initialized');
    });
    this.selfieSegmentation.onResults(this.onGetImageResults.bind(this));
  }

  private onGetImageResults(results: Results): void {
    this.canvasCtx.save();
    this.canvasCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // отрисовка маски лица
    this.canvasCtx.drawImage(results.segmentationMask, 0, 0, this.canvas.width, this.canvas.height);

    // отрисовка слоя с кадром, с вырезанием из него маски лица и установкой блюра
    this.canvasCtx.globalCompositeOperation = 'source-in';
    this.canvasCtx.drawImage(results.image, 0, 0, this.canvas.width, this.canvas.height);
    this.canvasCtx.filter = 'blur(0)';

    // отрисовка на пустом месте от маски незаблюренного изображения.
    this.canvasCtx.globalCompositeOperation = 'destination-atop';

    this.canvasCtx.filter = `blur(${this.blurValue}px)`;
    this.canvasCtx.drawImage(results.image, 0, 0, this.canvas.width, this.canvas.height);

    this.canvasCtx.restore();
  }

  private async processFrames(): Promise<void> {
    if (!this.incomingTrackProcessorReader) {
      return;
    }

    let lastTimestamp = 0;
    // eslint-disable-next-line no-constant-condition
    while (this.isStarted) {
      const { value: frame, done } = await this.incomingTrackProcessorReader.read();
      if (done) break;
      const now = performance.now();
      if (now - lastTimestamp >= MAX_BLUR_FRAME_RATE_MILLISECONDS) {
        const bitmap = await createImageBitmap(frame);
        if (this.isStarted) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          await this.selfieSegmentation.send({ image: bitmap });
        }
        // Обработка кадра (например, сегментация фона)
        lastTimestamp = now;
      }
      frame.close();
    }
  }

  async init(stream: MediaStream): Promise<MediaStream> {
    if (this.incomingStream && this.incomingStream.id === stream.id && this.processedStream) {
      this.isStarted = true;
      this.processFrames();
      return this.processedStream;
    }

    this.isStarted = false;
    this.incomingStream = stream;
    const track = this.incomingStream.getVideoTracks()[0];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const incomingTrackProcessor = new MediaStreamTrackProcessor({ track });
    this.incomingTrackProcessorReader = incomingTrackProcessor.readable.getReader();
    const mediaStream = this.canvas.captureStream(60);
    if (!this.processedStream) {
      this.processedStream = mediaStream;
    }

    this.isStarted = true;
    this.processFrames();

    return this.processedStream;
  }

  setBlurValue(value: number): void {
    this.blurValue = value;
  }

  setTrackConstraints(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  async destroy(): Promise<void> {
    this.isStarted = false;
  }

  async dispose(): Promise<void> {
    this.destroy();
    await this.selfieSegmentation.close();
    if (this.processedStream) {
      this.processedStream.getVideoTracks().forEach((track: MediaStreamTrack): void => {
        track.stop();
      });
    }
    this.incomingTrackProcessorReader?.cancel();
    this.incomingTrackProcessorReader = null;
  }
}
